// EncuestaFormSection.js — La encuesta de satisfacción como una sección de la
// página, no como popup: la persona la responde ahí mismo, con el header y el
// pie del sitio alrededor.
//
// El cuestionario NO se reescribe: es el mismo `EncuestaWizard` que usa el
// modal, en modo embebido, así que las preguntas salen del `encuesta` del
// certificado del proyecto y las respuestas se guardan donde siempre.
import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { encuestaService } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';
import { buildBg } from '../bgHelpers';
import { EncuestaWizard } from '../../EncuestaModal';

const Wrapper = styled.section`
  width: 100%;
  padding: ${p => p.$padding || '50px 2rem 80px'};
  background: ${p => p.$bg || '#ffffff'};
`;

const Inner = styled.div`
  max-width: ${p => p.$maxWidth || '900px'};
  margin: 0 auto;
`;

const Aviso = styled.div`
  text-align: center;
  color: ${p => p.$color || '#12345c'};
  font-size: ${p => p.$size || '19px'};
  line-height: 1.5;
`;

const Boton = styled.button`
  margin-top: 20px;
  background: ${p => p.$bg || '#0a7ab5'};
  color: ${p => p.$color || '#ffffff'};
  border: none;
  border-radius: ${p => p.$radio || '999px'};
  padding: ${p => p.$padding || '11px 30px'};
  font-family: inherit;
  font-size: ${p => p.$size || '16px'};
  font-weight: 600;
  cursor: pointer;
  &:hover { opacity: 0.9; }
`;

const EncuestaFormSection = ({ config, proyecto, tl }) => {
  const c = config || {};
  const t = (v, def = '') => (tl ? tl(v, def) : (v || def));
  const navigate = useNavigate();
  const { user } = useAuth();

  const claveCert = c.certificado_key || 'default';
  const [estado, setEstado] = useState(null);   // {requiere, ya_respondio, encuesta}
  const [enviando, setEnviando] = useState(false);

  const cargar = useCallback(() => {
    if (!user || !proyecto?.id) { setEstado(null); return; }
    encuestaService.estado(proyecto.id, claveCert)
      .then(res => setEstado(res.data || null))
      .catch(() => setEstado(null));
  }, [user, proyecto?.id, claveCert]);

  useEffect(() => { cargar(); }, [cargar]);

  const responder = async (respuestas) => {
    setEnviando(true);
    try {
      await encuestaService.responder(proyecto.id, claveCert, respuestas);
    } finally {
      setEnviando(false);
    }
  };

  const salir = () => {
    const url = c.url_al_terminar || '/';
    if (/^https?:\/\//i.test(url)) window.location.href = url;
    else navigate(url);
  };

  if (!user || !estado) return null;

  // Ya respondida: en vez del cuestionario, el agradecimiento y la salida.
  if (!estado.requiere) {
    if (c.ocultar_si_respondida === true) return null;
    return (
      <Wrapper $bg={buildBg(c, '#ffffff')} $padding={c.padding}>
        <Inner $maxWidth={c.ancho_maximo}>
          <Aviso $color={c.color_texto} $size={c.tamano_texto}>
            {t(c.texto_ya_respondida, 'Ya respondiste esta encuesta. ¡Gracias por tu tiempo!')}
          </Aviso>
          <div style={{ textAlign: 'center' }}>
            <Boton type="button" onClick={salir} $bg={c.color_boton} $radio={c.radio_boton}>
              {t(c.texto_boton_volver, 'Volver al curso')}
            </Boton>
          </div>
        </Inner>
      </Wrapper>
    );
  }

  return (
    <Wrapper $bg={buildBg(c, '#ffffff')} $padding={c.padding}>
      <Inner $maxWidth={c.ancho_maximo}>
        <EncuestaWizard
          embebido
          encuesta={estado.encuesta}
          color={c.color_acento || proyecto?.color_primario || '#0f355b'}
          submitting={enviando}
          onSubmit={responder}
          onFinalizar={salir}
        />
      </Inner>
    </Wrapper>
  );
};

export default EncuestaFormSection;
